import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, Clock, Star, Users, Award, ArrowRight } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ComparisonTool = ({ roadmaps, onClose, onClear }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-600 bg-green-100';
      case 'Intermediate': return 'text-blue-600 bg-blue-100';
      case 'Advanced': return 'text-purple-600 bg-purple-100';
      case 'Expert': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const comparisonFeatures = [
    { key: 'difficulty', label: 'Difficulty Level', icon: <Star className="w-4 h-4" /> },
    { key: 'estimatedTime', label: 'Duration', icon: <Clock className="w-4 h-4" /> },
    { key: 'missions', label: 'Total Missions', icon: <Check className="w-4 h-4" /> },
    { key: 'technology', label: 'Technology', icon: <Check className="w-4 h-4" /> },
    { key: 'industry', label: 'Industry Focus', icon: <Check className="w-4 h-4" /> },
    { key: 'rating', label: 'Rating', icon: <Star className="w-4 h-4" /> },
    { key: 'enrolled', label: 'Enrolled Students', icon: <Users className="w-4 h-4" /> },
    { key: 'certifications', label: 'Certifications', icon: <Award className="w-4 h-4" /> }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-card rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold">Compare Roadmaps</h2>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClear}>
              Clear All
            </Button>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {/* Roadmap Headers */}
            <div className="flex border-b border-border">
              <div className="w-48 p-4 bg-muted/50">
                <span className="font-medium text-sm">Feature</span>
              </div>
              {roadmaps?.map((roadmap) => (
                <div key={roadmap?.id} className="w-64 p-4 text-center border-l border-border">
                  <div className={`w-10 h-10 ${roadmap?.color} rounded-lg flex items-center justify-center text-white mx-auto mb-2`}>
                    {roadmap?.icon}
                  </div>
                  <h3 className="font-medium text-sm mb-1 line-clamp-2">{roadmap?.title}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(roadmap?.difficulty)}`}>
                    {roadmap?.difficulty}
                  </span>
                </div>
              ))}
            </div>

            {/* Feature Rows */}
            {comparisonFeatures?.map((feature, index) => (
              <div key={feature?.key} className={`flex ${index % 2 === 0 ? 'bg-muted/20' : ''}`}>
                <div className="w-48 p-4 border-t border-border bg-muted/50">
                  <div className="flex items-center space-x-2">
                    {feature?.icon}
                    <span className="font-medium text-sm">{feature?.label}</span>
                  </div>
                </div>
                {roadmaps?.map((roadmap) => (
                  <div key={roadmap?.id} className="w-64 p-4 text-center border-l border-t border-border">
                    {feature?.key === 'difficulty' && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(roadmap?.difficulty)}`}>
                        {roadmap?.difficulty}
                      </span>
                    )}
                    {feature?.key === 'estimatedTime' && (
                      <span className="text-sm">{roadmap?.estimatedTime}</span>
                    )}
                    {feature?.key === 'missions' && (
                      <span className="text-sm font-medium">{roadmap?.missions}</span>
                    )}
                    {feature?.key === 'technology' && (
                      <span className="text-xs bg-muted px-2 py-1 rounded-full">{roadmap?.technology}</span>
                    )}
                    {feature?.key === 'industry' && (
                      <span className="text-xs text-muted-foreground">{roadmap?.industry}</span>
                    )}
                    {feature?.key === 'rating' && (
                      <div className="flex items-center justify-center space-x-1">
                        <Star className="w-3 h-3 text-yellow-500" />
                        <span className="text-sm font-medium">{roadmap?.rating}</span>
                      </div>
                    )}
                    {feature?.key === 'enrolled' && (
                      <span className="text-sm">{(roadmap?.enrolled / 1000)?.toFixed(1)}K</span>
                    )}
                    {feature?.key === 'certifications' && (
                      <div className="space-y-1">
                        {roadmap?.certifications?.slice(0, 2)?.map((cert, i) => (
                          <div key={i} className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                            {cert}
                          </div>
                        ))}
                        {roadmap?.certifications?.length > 2 && (
                          <div className="text-xs text-muted-foreground">
                            +{roadmap?.certifications?.length - 2} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {/* Key Outcomes Section */}
            <div className="flex">
              <div className="w-48 p-4 border-t border-border bg-muted/50">
                <span className="font-medium text-sm">Key Outcomes</span>
              </div>
              {roadmaps?.map((roadmap) => (
                <div key={roadmap?.id} className="w-64 p-4 border-l border-t border-border">
                  <div className="space-y-2">
                    {roadmap?.outcomes?.slice(0, 4)?.map((outcome, i) => (
                      <div key={i} className="flex items-start space-x-2 text-xs">
                        <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground line-clamp-2">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex">
              <div className="w-48 p-4 border-t border-border bg-muted/50">
                <span className="font-medium text-sm">Get Started</span>
              </div>
              {roadmaps?.map((roadmap) => (
                <div key={roadmap?.id} className="w-64 p-4 border-l border-t border-border">
                  <Button
                    size="sm"
                    className="w-full flex items-center justify-center space-x-2"
                    onClick={() => {
                      // Navigate to roadmap enrollment
                      window.location.href = `/mission-map-dashboard?roadmap=${roadmap?.slug}`;
                    }}
                  >
                    <span>Start Learning</span>
                    <ArrowRight className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Comparing {roadmaps?.length} roadmaps • Choose the path that best fits your goals
            </p>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={onClose}>
                Close Comparison
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ComparisonTool;